import { Immutable } from 'nuclear-js';

import Actions from 'data/actions';
import Analytics from 'utils/analytics';
import Clients from 'http-clients';
import Getters from 'data/getters';
import Reactor from 'data/reactor';
import { courses } from '@nwea/subject-course-data';
import CommonValues from 'resources/common-values';
import { removeOverlappingTestEvents, trackTestEventOverlaps } from './utils';
import { populateHighlightsWithStudentInfo } from '../utils/students';
import { jwtDecode } from "jwt-decode";

/**
 *
 * @param apiCallName - name of api to include in the log
 * @param shouldRetryFn - function to determine whether to retry
 * @param retryFn - function to run on retry
 * @param optionalErrorReturnValue - optional value to return when retries are exhausted (defaults to the error)
 * @returns {(function(*): (*))|*} - returns optionalErrorReturnValue (or error)
 */
const createFailureCallbackWithRetry = (apiCallName, shouldRetryFn, retryFn, optionalErrorReturnValue = null) => (
    error,
    url
) => {
    const response = error.response;
    const status = response && response.status ? response.status : 'unknown';
    const data = response && response.data ? JSON.stringify(response.data) : 'no data';
    if (shouldRetryFn()) {
        console.log(
            'retrying call for',
            apiCallName,
            ', url:',
            url,
            '. Message:',
            error.message,
            ', status:',
            status,
            ', data:',
            data
        );
        return retryFn();
    } else {
        console.error(
            'ERROR: call to endpoint',
            apiCallName,
            'url',
            url,
            'failed and retries exhausted.  Error message:',
            error.message,
            ', status:',
            status,
            ', data:',
            data
        );
        const returnVal = optionalErrorReturnValue != null ? optionalErrorReturnValue : error;
        return returnVal;
    }
};

export default {
    populateTermsForPartner: function(
        partnerBid = Reactor.evaluate(Getters.partner.partnerBid),
        optionalFailureCallback = null
    ) {
        const callback = (terms) => {
            Actions.partner.storeTermsInformation(terms);
        };

        return Clients.partnerConfig.getTermsForPartner(partnerBid, callback, optionalFailureCallback);
    },
    getAcademicYearForPartner: function(partnerBid = Reactor.evaluate(Getters.partner.partnerBid)) {
        const callback = (currentAcedemicYear) => {
            Actions.partner.storeCurrentAcademicYearInformation(currentAcedemicYear);
        };

        return Clients.partnerConfig.getAcademicYearForPartner(partnerBid, callback);
    },
    getUserProfile: function(callback, failureCallback, callCount = 1) {
        // this one is a little different, no callback is provided
       
        const onFailure = (error) => {
            if (callCount < 3) {
                return this.getUserProfile(callback, failureCallback, callCount + 1);
            } else {
                console.error(`getUserProfile failed: ${callCount} times`, error);
                return failureCallback();
            }
        };
        return Clients.auth.getUserProfile(callback, onFailure);
        
        /*
        const profileData =sessionStorage.getItem("auth_session_Token");
        const decoded = {
            "additionalErrorInfo": null,
            "errorMessage": null,
            "userProfile": {
            "partnerBid": "4f66449d-f4fa-4bae-bf8c-8ae0bf0d0355",
            "partnerId": 486,
            "userBid": "ffe03a27-f117-4d40-84bd-c307031d9e4b",
            "userId": 10463,
            "firstName": "Barbara",
            "middleName": null,
            "lastName": "Minshew",
            "username": "Minshew",
            "emailAddress": "minshewb@nweatest.org",
            "skillsNavigatorEnabled": false,
            "roles": [
                "INSTRUCTIONAL_PROVIDER",
                "SYSTEM_ADMINISTRATOR",
                "DATA_ADMINISTRATOR",
                "PROCTOR",
                "ASSESSMENT_COORDINATOR"
            ],
            "hmhRefIds": null,
            "agencyCode": null,
            "enabledApplications": [
                "MAP"
            ]
            },
            "educatorLoginUrl": null,
            "studentLoginUrl": null
            };//jwtDecode(profileData);

       // if(decoded != undefined || decoded != null ){
            console.log("IN return getUserProfile ::"+JSON.stringify(decoded))
           return new Promise(function(resolve, reject) {
            resolve( callback({
                "additionalErrorInfo": null,
                "errorMessage": null,
                "userProfile": {
                "partnerBid": "4f66449d-f4fa-4bae-bf8c-8ae0bf0d0355",
                "partnerId": 486,
                "userBid": "ffe03a27-f117-4d40-84bd-c307031d9e4b",
                "userId": 10463,
                "firstName": "Barbara",
                "middleName": null,
                "lastName": "Minshew",
                "username": "Minshew",
                "emailAddress": "minshewb@nweatest.org",
                "skillsNavigatorEnabled": false,
                "roles": [
                    "INSTRUCTIONAL_PROVIDER",
                    "SYSTEM_ADMINISTRATOR",
                    "DATA_ADMINISTRATOR",
                    "PROCTOR",
                    "ASSESSMENT_COORDINATOR"
                ],
                "hmhRefIds": null,
                "agencyCode": null,
                "enabledApplications": [
                    "MAP"
                ]
                },
                "educatorLoginUrl": null,
                "studentLoginUrl": null
                }))
          });
           
      /*  }else {
            return failureCallback();
        }
            
       */
    },
    getLinksForUser: function(partnerBid) {
        const rolesCallback = (res) => {
            Actions.user.storeUserLinks(res);
        };

        return Clients.userProfile.getLinks(partnerBid, rolesCallback);
    },
    getSchoolsForInstructorAndTerm(partnerBid, termBid, instructorBid) {
        const callback = (schools) => Actions.roster.storeSchools(schools);

        return Clients.reportingApi.getSchools(partnerBid, termBid, instructorBid, callback);
    },
    getInstructorsForSchoolAndTerm(partnerBid, schoolBid, termBid) {
        const callback = (schools) => Actions.roster.storeInstructors(schools);

        return Clients.reportingApi.getInstructors(partnerBid, schoolBid, termBid, callback);
    },
    getClassesForInstructor(partnerBid, schoolBid, instructorBid, termBid, optionalFailureCallback = null) {
        const callback = (classes) => Actions.roster.storeClasses(classes);

        return Clients.reportingApi.getClasses(
            partnerBid,
            schoolBid,
            instructorBid,
            termBid,
            callback,
            optionalFailureCallback
        );
    },
    getStudentsForClass(partnerBid, schoolBid, classBid, termBid, cb = () => {}, optionalFailureCallback = null) {
        const callback = (students) => {
            Actions.roster.storeStudents(students);
            cb(students);
        };

        return Clients.reportingApi.getStudents(
            partnerBid,
            schoolBid,
            classBid,
            termBid,
            callback,
            optionalFailureCallback
        );
    },

    /* This gets a little tricky...
     * In RDB's measurement_scale table and the subject-course-data repo,
     * we can relate each course/measurementScale to a specific "Discipline Name",
     * which is typically referred to as "Subject" in this repo. Student Profile does not
     * follow this convention completely - it considers Reading, Language Usage, and Spanish Reading
     * (courses under the Language Arts discipline) to be their own subjects, and refers to them as subjects
     * throughout the codebase.
     *
     * Therefore, we need to coerce those courses into subjects before consulting the subject-course-data
     * repo to assign course-to-subject relations for other courses.
     * */
    getCombinedStudentInfo(partnerBid, studentBid, callCount = 1) {
        const callback = (combinedInfo) => {
            /* We're going to modify each test event and put the subject key
             * (which can actually contain measurementScaleIDs) on the measurementScaleID
             * key. Then we derive the subject associated with a given measurementScaleID
             * and assign that to the subject key.
             *
             * The purpose for doing so is because the incoming test events (TEs) have a key
             * 'subject' - but the value of said key is actually a 'measurementScaleID'.
             * Now that we are supporting TEs with a course other than the 5 subjects in the UI,
             * we need to ensure that measurementScaleID and the subjectBid that is associated to
             * that measurementScaleID are both included in the TE object, so that we can display
             * the TE under its given subject (for backwards compatability within the app),
             * but also use the measurementScaleID when requesting things like norms data, etc.
             *
             * An example of the resulting payload can be found in the unit tests for this file.
             * */
            let testEventOverlaps = {};
            combinedInfo.testEvents.forEach((testEvent) => {
                let course = courses.find((courseObj) => {
                    if (testEvent.subject === 12) {
                        //Converting 12 (spanish reading) to 7 which the course-subject module uses as an msid
                        return courseObj.measurementScaleID === '7';
                    } else {
                        return parseInt(courseObj.measurementScaleID) === testEvent.subject;
                    }
                });

                const isCourseSpecific = CommonValues.isCourseSpecificMSID(testEvent.subject);
                testEvent.isCourseSpecific = isCourseSpecific;
                testEvent.measurementScaleID = testEvent.subject;

                if (course) {
                    if (isCourseSpecific) {
                        testEvent.subject = parseInt(course.subjectBid);
                    }
                    testEvent.courseName = course.name;
                } else {
                    // We'll allow this TE to be placed in the store, but it will not be accessible to the UI
                    console.error('Course not found in subject-course-data for test event: ' + testEvent.testEventBid);
                    testEvent.courseName = 'COURSE NOT FOUND';
                }

                testEventOverlaps = trackTestEventOverlaps(testEvent, testEventOverlaps);
            });

            /* removeOverlappingTestEvents will remove the earlier (defined by testDate) test event
             * in the case of an 'overlap' - where overlap is defined as two test events occurring in the same
             * term for the same subjectBid (ex: sBid: 1 and sBid 100 both occurring in term 202104)*/
            const { cleanedTestEvents, removedTestEvents } = removeOverlappingTestEvents(
                combinedInfo.testEvents,
                testEventOverlaps
            );
            combinedInfo.testEvents = cleanedTestEvents;
            combinedInfo.removedTestEvents = removedTestEvents;
            combinedInfo.measurementScaleIDsForStudent = combinedInfo.testEvents.reduce((msidArray, testEvent) => {
                if (!msidArray.includes(testEvent.measurementScaleID)) {
                    msidArray.push(testEvent.measurementScaleID);
                }
                return msidArray;
            }, []);
            Actions.student.loadCombinedInfo(studentBid, combinedInfo);
            return combinedInfo;
        };
        const shouldRetryFn = () => callCount < 3;
        const retryFn = () => {
            return this.getCombinedStudentInfo(partnerBid, studentBid, callCount + 1);
        };
        const failureCallback = createFailureCallbackWithRetry('getCombinedStudentInfo', shouldRetryFn, retryFn);

        return Clients.gateway.getCombinedStudentInformation(partnerBid, studentBid, callback, failureCallback);
    },
    getLinkingStudiesForPartner: function(partnerBid, callCount = 1) {
        const callback = (data) => {
            Actions.partner.storeLinkingStudiesForPartner(data);
        };
        const shouldRetryFn = () => callCount < 3;
        const retryFn = () => {
            return this.getLinkingStudiesForPartner(partnerBid, callCount + 1);
        };
        const failureCallback = createFailureCallbackWithRetry('getLinkingStudiesForPartner', shouldRetryFn, retryFn);
        return Clients.gateway.linkingStudiesForPartner(partnerBid, callback, failureCallback);
    },
    getLinkingStudies: function(studentBid, subjectBid, requestPayload, callCount = 1) {
        const callback = (data) => {
            const linkingStudyData = data === '' ? Immutable.List() : data;
            return () => {
                Actions.student.storeLinkingStudies(studentBid, subjectBid, linkingStudyData);
            };
        };
        const shouldRetryFn = () => callCount < 3;
        const retryFn = () => {
            return this.getLinkingStudies(studentBid, subjectBid, requestPayload, callCount + 1);
        };
        const failureCallback = createFailureCallbackWithRetry('getLinkingStudies', shouldRetryFn, retryFn);

        return Clients.gateway.postLinkingStudies(requestPayload, callback, failureCallback);
    },
    getRitQuintiles: function(requestPayload, callCount = 1) {
        const callback = (data) => {
            return () => {
                Actions.partner.storeRitQuintiles(data);
            };
        };
        const shouldRetryFn = () => callCount < 3;
        const retryFn = () => {
            return this.getRitQuintiles(requestPayload, callCount + 1);
        };
        const failureCallback = createFailureCallbackWithRetry('getRitQuintiles', shouldRetryFn, retryFn);

        return Clients.gateway.getRitQuintiles(requestPayload, callback, failureCallback);
    },
    getDistrictAverages: function(partnerBid, requestPayload, callCount = 1) {
        const callback = (averages) => {
            return () => {
                Actions.partner.storeDistrictAverages(averages);
            };
        };
        const shouldRetryFn = () => callCount < 3;
        const retryFn = () => {
            return this.getDistrictAverages(partnerBid, requestPayload, callCount + 1);
        };
        const failureCallback = createFailureCallbackWithRetry('getDistrictAverages', shouldRetryFn, retryFn);

        return Clients.gateway.districtAverages(partnerBid, requestPayload, callback, failureCallback);
    },
    getGrowthProjections: function(studentBid, requestPayload, callCount = 1) {
        const callback = (projections) => {
            return () => {
                Actions.student.loadGrowthProjections(studentBid, projections);
            };
        };
        const shouldRetryFn = () => callCount < 3;
        const retryFn = () => {
            return this.getGrowthProjections(studentBid, requestPayload, callCount + 1);
        };
        const retriesExhaustedReturnValue = () => {
            Actions.student.createEmptyGrowthProjectionsForStudent(studentBid);
        };
        const failureCallback = createFailureCallbackWithRetry(
            'getGrowthProjections',
            shouldRetryFn,
            retryFn,
            retriesExhaustedReturnValue
        );

        return Clients.gateway.getGrowthProjections(requestPayload, callback, failureCallback);
    },
    getGrowthPercentiles: function(studentBid, requestPayload, callCount = 1) {
        const callback = (projections) => {
            return () => {
                Actions.student.loadGrowthPercentiles(studentBid, projections);
            };
        };
        const shouldRetryFn = () => callCount < 3;
        const retryFn = () => {
            return this.getGrowthPercentiles(studentBid, requestPayload, callCount + 1);
        };
        const retriesExhaustedReturnValue = () => {
            Actions.student.createEmptyGrowthPercentilesForStudent(studentBid);
        };
        const failureCallback = createFailureCallbackWithRetry(
            'getGrowthPercentiles',
            shouldRetryFn,
            retryFn,
            retriesExhaustedReturnValue
        );

        return Clients.gateway.getGrowthPercentiles(requestPayload, callback, failureCallback);
    },
    getLearningContinuumData: function(testKey, callCount = 1) {
        const callback = (lcData) => {
            Actions.learningContinuum.storeLearningContinuum(testKey, lcData);
            return lcData;
        };
        const shouldRetryFn = () => callCount < 3;
        const retryFn = () => {
            return this.getLearningContinuumData(testKey, callCount + 1);
        };
        const retriesExhaustedReturnValue = () => {
            Actions.learningContinuum.storeLearningContinuum(testKey, {});
        };
        const failureCallback = createFailureCallbackWithRetry(
            'getLearningContinuumData',
            shouldRetryFn,
            retryFn,
            retriesExhaustedReturnValue
        );

        return Clients.reportingApi.getLearningContinuumData(testKey, callback, failureCallback);
    },
    getNormDataForSettingPerformanceGoals: function(studentBid, subject, termNumber, request, callCount = 1) {
        const callback = (result) => {
            return () => {
                Actions.student.loadNormDataForGoalSetting(studentBid, termNumber, subject, result);
            };
        };
        const shouldRetryFn = () => callCount < 3;
        const retryFn = () => {
            return this.getNormDataForSettingPerformanceGoals(studentBid, subject, termNumber, request, callCount + 1);
        };
        const retriesExhaustedReturnValue = () => {
            Actions.student.createEmptyNormDataForGoalSetting(studentBid, termNumber, subject);
        };
        const failureCallback = createFailureCallbackWithRetry(
            'getNormDataForSettingPerformanceGoals',
            shouldRetryFn,
            retryFn,
            retriesExhaustedReturnValue
        );

        return Clients.gateway.statusPreview(request, callback, failureCallback);
    },
    getNormDataForSettingGrowthGoals: function(studentBid, subject, termNumber, request, callCount = 1) {
        const callback = (result) => {
            return () => {
                Actions.student.loadNormDataForGoalSetting(studentBid, termNumber, subject, result);
            };
        };
        const shouldRetryFn = () => callCount < 3;
        const retryFn = () => {
            return this.getNormDataForSettingGrowthGoals(studentBid, subject, termNumber, request, callCount + 1);
        };
        const retriesExhaustedReturnValue = () => {
            Actions.student.createEmptyNormDataForGoalSetting(studentBid, termNumber, subject);
        };
        const failureCallback = createFailureCallbackWithRetry(
            'getNormDataForSettingGrowthGoals',
            shouldRetryFn,
            retryFn,
            retriesExhaustedReturnValue
        );

        return Clients.gateway.growthPreview(request, callback, failureCallback);
    },

    postGrowthGoal: function(partnerBid, studentBid, payload, callCount = 1) {
        const callback = (goal) => {
            return {
                goal,
                success: true,
            };
        };
        const shouldRetryFn = () => callCount < 3;
        const retryFn = () => {
            return this.postGrowthGoal(partnerBid, studentBid, payload, callCount + 1);
        };
        const retriesExhaustedReturnValue = {
            success: false,
        };
        const failureCallback = createFailureCallbackWithRetry(
            'postGrowthGoal',
            shouldRetryFn,
            retryFn,
            retriesExhaustedReturnValue
        );

        return Clients.gateway.postGrowthGoal(partnerBid, studentBid, payload, callback, failureCallback);
    },
    putGrowthGoal: function(goal, growthGoalBid, callCount = 1) {
        const callback = (goal) => {
            return {
                goal,
                success: true,
            };
        };
        const shouldRetryFn = () => callCount < 3;
        const retryFn = () => {
            return this.putGrowthGoal(goal, growthGoalBid, callCount + 1);
        };
        const retriesExhaustedReturnValue = {
            success: false,
        };
        const failureCallback = createFailureCallbackWithRetry(
            'putGrowthGoal',
            shouldRetryFn,
            retryFn,
            retriesExhaustedReturnValue
        );

        return Clients.gateway.putGrowthGoal(goal, growthGoalBid, callback, failureCallback);
    },
    deleteGrowthGoal: function(growthGoalBid, callCount = 1) {
        const callback = (data) => {
            return true;
        };
        const shouldRetryFn = () => callCount < 3;
        const retryFn = () => {
            return this.deleteGrowthGoal(growthGoalBid, callCount + 1);
        };
        const retriesExhaustedReturnValue = false;
        const failureCallback = createFailureCallbackWithRetry(
            'deleteGrowthGoal',
            shouldRetryFn,
            retryFn,
            retriesExhaustedReturnValue
        );

        return Clients.gateway.deleteGrowthGoal(growthGoalBid, callback, failureCallback);
    },
    getHighlights: function(partnerBid, studentBid, callCount = 1) {
        const callback = (data) => {
            if (data && data.highlights) {
                const highlights = populateHighlightsWithStudentInfo(studentBid, data.highlights);
                return () => {
                    Actions.student.storeHighlightsData(studentBid, highlights);
                };
            }

            Analytics.logErrorToDatadog(
                `getHighlights endpoint returned unexpected data: ${JSON.stringify(data)}.  Storing empty data.`
            );
            return Promise.resolve(() => {
                Actions.student.storeHighlightsData(studentBid, {});
            });
        };
        const shouldRetryFn = () => callCount < 3;
        const retryFn = () => {
            return this.getHighlights(partnerBid, studentBid, callCount + 1);
        };
        const retriesExhaustedReturnValue = () => {
            Actions.student.storeHighlightsData(studentBid, {});
        };
        const failureCallback = createFailureCallbackWithRetry(
            'getHighlights',
            shouldRetryFn,
            retryFn,
            retriesExhaustedReturnValue
        );

        return Clients.gateway.highlightsForStudent(partnerBid, studentBid, callback, failureCallback);
    },
};
